import { useState, useEffect } from "react";
import { useApi } from "@/lib/useApi";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/lib/i18n";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { PageHeader } from "@/components/common";
import { applyBrandColors, fileToDataUrl } from "@/lib/theme";
import { Settings as SettingsIcon, Palette, ShieldCheck, Upload, Save, UserCog, LifeBuoy, ListChecks, GripVertical } from "lucide-react";
import { toast } from "sonner";
import { NAV_SECTIONS } from "@/components/AppShell";

function MenuCustomizer() {
  const { refreshUser } = useAuth();
  const { data: company } = useApi("/company");
  const ALL = NAV_SECTIONS.flatMap((s) => s.items).filter((n) => !n.masterOnly);
  const LOCKED = ["/installningar", "/dashboard"];
  const [hidden, setHidden] = useState([]);
  const [order, setOrder] = useState([]);
  const [dragTo, setDragTo] = useState(null);
  useEffect(() => {
    if (!company) return;
    setHidden(company.menu_hidden || []);
    const saved = (company.menu_order || []).filter((to) => ALL.some((n) => n.to === to));
    setOrder([...saved, ...ALL.map((n) => n.to).filter((to) => !saved.includes(to))]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company]);
  const toggle = (to) => setHidden((h) => (h.includes(to) ? h.filter((x) => x !== to) : [...h, to]));
  const onDrop = (to) => {
    if (!dragTo || dragTo === to) return setDragTo(null);
    setOrder((o) => { const a = [...o]; a.splice(a.indexOf(dragTo), 1); a.splice(a.indexOf(to), 0, dragTo); return a; });
    setDragTo(null);
  };
  const save = async () => {
    try { await api.patch("/company", { menu_hidden: hidden, menu_order: order }); await refreshUser(); toast.success("Meny uppdaterad"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const byTo = Object.fromEntries(ALL.map((n) => [n.to, n]));
  return (
    <Card className="max-w-2xl p-0" data-testid="menu-customizer">
      <div className="border-b p-4 text-sm text-muted-foreground">Dra i handtaget för att ändra ordning och bocka i/ur vad som ska synas. Dashboard & Inställningar är alltid synliga. Punkter utanför er verksamhetstyp eller valda moduler göms ändå automatiskt.</div>
      <div className="divide-y">
        {order.map((to) => {
          const n = byTo[to];
          if (!n) return null;
          const locked = LOCKED.includes(to);
          return (
            <div key={to} draggable onDragStart={() => setDragTo(to)} onDragEnd={() => setDragTo(null)}
              onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(to)}
              className={`flex items-center gap-3 p-3 ${dragTo === to ? "bg-secondary" : ""}`} data-testid={`menu-item-${to.slice(1)}`}>
              <GripVertical className="h-4 w-4 cursor-grab text-muted-foreground" data-testid={`menu-drag-${to.slice(1)}`} />
              <Checkbox checked={locked || !hidden.includes(to)} disabled={locked} onCheckedChange={() => toggle(to)} />
              <span className={`flex-1 text-sm ${locked ? "text-muted-foreground" : ""}`}>{n.label}{locked ? " (alltid på)" : ""}</span>
            </div>
          );
        })}
      </div>
      <div className="border-t p-4"><Button onClick={save} data-testid="save-menu"><Save className="mr-1 h-4 w-4" />Spara meny</Button></div>
    </Card>
  );
}

function Branding() {
  const { t } = useLang();
  const { refreshUser } = useAuth();
  const { data: company } = useApi("/company");
  const [form, setForm] = useState({ name: "", logo_data: "", color_primary: "#0F2942", color_accent: "#2563EB" });
  useEffect(() => {
    if (company) setForm({
      name: company.name || "", logo_data: company.logo_data || "",
      color_primary: company.color_primary || "#0F2942", color_accent: company.color_accent || "#2563EB",
    });
  }, [company]);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try { const d = await fileToDataUrl(f); setForm((s) => ({ ...s, logo_data: d })); }
    catch (err) { toast.error(err.message); }
  };
  const save = async () => {
    try {
      await api.put("/company/branding", form);
      applyBrandColors(form.color_primary, form.color_accent);
      await refreshUser();
      toast.success(t("brand_saved"));
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <Card className="max-w-2xl p-6">
      <div className="grid gap-5">
        <div><Label>{t("org_name_lbl")}</Label>
          <Input value={form.name} onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))} data-testid="brand-name" /></div>
        <div>
          <Label>{t("logo_lbl")}</Label>
          <div className="mt-1 flex items-center gap-4">
            <div className="grid h-16 w-16 place-items-center overflow-hidden rounded-xl border bg-secondary">
              {form.logo_data ? <img src={form.logo_data} alt="logo" className="h-full w-full object-contain" /> : <Upload className="h-5 w-5 text-muted-foreground" />}
            </div>
            <label className="cursor-pointer rounded-md border px-3 py-2 text-sm hover:bg-secondary">
              {t("choose_img")} <input type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="brand-logo-input" />
            </label>
            {form.logo_data && <Button variant="ghost" size="sm" onClick={() => setForm((s) => ({ ...s, logo_data: "" }))}>{t("delete")}</Button>}
          </div>
          <p className="mt-1 text-xs text-muted-foreground">{t("logo_hint")}</p>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div><Label>{t("primary_color")}</Label>
            <div className="mt-1 flex items-center gap-2">
              <input type="color" value={form.color_primary} onChange={(e) => setForm((s) => ({ ...s, color_primary: e.target.value }))} className="h-10 w-14 rounded border" data-testid="brand-primary" />
              <Input value={form.color_primary} onChange={(e) => setForm((s) => ({ ...s, color_primary: e.target.value }))} className="font-mono" />
            </div></div>
          <div><Label>{t("accent_color")}</Label>
            <div className="mt-1 flex items-center gap-2">
              <input type="color" value={form.color_accent} onChange={(e) => setForm((s) => ({ ...s, color_accent: e.target.value }))} className="h-10 w-14 rounded border" data-testid="brand-accent" />
              <Input value={form.color_accent} onChange={(e) => setForm((s) => ({ ...s, color_accent: e.target.value }))} className="font-mono" />
            </div></div>
        </div>
        <div className="flex items-center gap-3 rounded-lg border p-3">
          <span className="rounded-md px-3 py-1.5 text-sm font-semibold text-white" style={{ background: form.color_primary }}>{t("primary_word")}</span>
          <span className="rounded-md px-3 py-1.5 text-sm font-semibold text-white" style={{ background: form.color_accent }}>{t("accent_word")}</span>
          <span className="text-xs text-muted-foreground">{t("preview")}</span>
        </div>
        <Button onClick={save} className="w-fit" data-testid="save-branding"><Save className="mr-1 h-4 w-4" />{t("save_appearance")}</Button>
      </div>
    </Card>
  );
}

function Permissions() {
  const { t } = useLang();
  const { data: catalog } = useApi("/permissions/catalog");
  const { data: grants, reload } = useApi("/permissions/role-grants");
  const [state, setState] = useState({});
  useEffect(() => { if (grants) setState(grants); }, [grants]);

  const toggle = (role, perm) => setState((s) => {
    const cur = new Set(s[role] || []);
    cur.has(perm) ? cur.delete(perm) : cur.add(perm);
    return { ...s, [role]: [...cur] };
  });
  const save = async () => {
    try { await api.put("/permissions/role-grants", { role_grants: state }); reload(); toast.success(t("perms_saved")); }
    catch (e) { toast.error(apiErr(e)); }
  };
  if (!catalog) return <p className="text-muted-foreground">{t("loading")}</p>;

  return (
    <Card className="p-0">
      <div className="border-b p-4 text-sm text-muted-foreground">{t("perms_intro")}</div>
      <div className="overflow-x-auto kr-scroll">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b bg-secondary/50">
              <th className="sticky left-0 z-10 bg-secondary/50 px-4 py-3 text-left font-semibold">{t("perm_col")}</th>
              {catalog.roles.map((r) => <th key={r.key} className="min-w-[120px] px-3 py-3 text-center text-xs font-medium">{r.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {catalog.permissions.map((p) => (
              <tr key={p.key} className="border-b hover:bg-secondary/30">
                <td className="sticky left-0 z-10 bg-card px-4 py-2 font-medium">{p.label}</td>
                {catalog.roles.map((r) => (
                  <td key={r.key} className="px-3 py-2 text-center">
                    <Checkbox checked={(state[r.key] || []).includes(p.key)} onCheckedChange={() => toggle(r.key, p.key)}
                      data-testid={`grant-${r.key}-${p.key}`} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="border-t p-4"><Button onClick={save} data-testid="save-grants"><Save className="mr-1 h-4 w-4" />{t("save_perms")}</Button></div>
    </Card>
  );
}

function Moderators() {
  const { t } = useLang();
  const { data: mods, reload } = useApi("/feed/moderators");
  const toggle = async (id, enabled) => {
    try { await api.post("/feed/moderators", { user_id: id, enabled }); reload(); toast.success(t("saved")); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <Card className="max-w-2xl p-0">
      <div className="border-b p-4 text-sm text-muted-foreground">{t("mods_intro")}</div>
      <div className="divide-y">
        {(mods || []).map((u) => (
          <div key={u.id} className="flex items-center gap-3 p-4" data-testid={`mod-row-${u.id}`}>
            <div className="flex-1">
              <div className="text-sm font-medium">{u.name || u.email}</div>
              <div className="text-xs text-muted-foreground">{u.email}</div>
            </div>
            <Switch checked={u.is_moderator} onCheckedChange={(v) => toggle(u.id, v)} data-testid={`mod-switch-${u.id}`} />
          </div>
        ))}
        {(mods || []).length === 0 && <div className="p-4 text-sm text-muted-foreground">{t("no_users")}</div>}
      </div>
    </Card>
  );
}

function SupportContacts() {
  const { t } = useLang();
  const { data: mods, reload } = useApi("/support/contacts");
  const toggle = async (id, enabled) => {
    try { await api.post("/support/contacts", { user_id: id, enabled }); reload(); toast.success(t("saved")); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <Card className="max-w-2xl p-0">
      <div className="border-b p-4 text-sm text-muted-foreground">{t("support_intro")}</div>
      <div className="divide-y">
        {(mods || []).map((u) => (
          <div key={u.id} className="flex items-center gap-3 p-4" data-testid={`support-row-${u.id}`}>
            <div className="flex-1">
              <div className="text-sm font-medium">{u.name || u.email}</div>
              <div className="text-xs text-muted-foreground">{u.email}</div>
            </div>
            <Switch checked={u.is_support} onCheckedChange={(v) => toggle(u.id, v)} data-testid={`support-switch-${u.id}`} />
          </div>
        ))}
        {(mods || []).length === 0 && <div className="p-4 text-sm text-muted-foreground">{t("no_users")}</div>}
      </div>
    </Card>
  );
}

export default function Settings() {
  const { t } = useLang();
  return (
    <div>
      <PageHeader title={t("set_title")} icon={SettingsIcon} subtitle={t("set_sub")} />
      <Tabs defaultValue="brand">
        <TabsList>
          <TabsTrigger value="brand" data-testid="tab-brand"><Palette className="mr-1 h-4 w-4" />{t("tab_brand")}</TabsTrigger>
          <TabsTrigger value="menu" data-testid="tab-menu"><ListChecks className="mr-1 h-4 w-4" />Meny</TabsTrigger>
          <TabsTrigger value="perms" data-testid="tab-perms"><ShieldCheck className="mr-1 h-4 w-4" />{t("tab_perms")}</TabsTrigger>
          <TabsTrigger value="mods" data-testid="tab-mods"><UserCog className="mr-1 h-4 w-4" />{t("tab_mods")}</TabsTrigger>
          <TabsTrigger value="support" data-testid="tab-support"><LifeBuoy className="mr-1 h-4 w-4" />{t("tab_support")}</TabsTrigger>
        </TabsList>
        <TabsContent value="brand" className="mt-4"><Branding /></TabsContent>
        <TabsContent value="menu" className="mt-4"><MenuCustomizer /></TabsContent>
        <TabsContent value="perms" className="mt-4"><Permissions /></TabsContent>
        <TabsContent value="mods" className="mt-4"><Moderators /></TabsContent>
        <TabsContent value="support" className="mt-4"><SupportContacts /></TabsContent>
      </Tabs>
    </div>
  );
}
